import { useState } from 'react';
import cx from 'classnames';

import { IconEdit2_16 } from 'src/assets/icons/16x16/IconEdit2_16';
import { IconTrash16 } from 'src/assets/icons/16x16/IconTrash16';
import { IconCheck_24 } from 'src/assets/icons/24x24/IconCheck_24';
import { IconMoreHorizontal_24 } from 'src/assets/icons/24x24/IconMoreHorizontal_24';
// @ts-expect-error - no TypeScript definitions yet
import { ContextMenu } from 'src/components/ContextMenu';
import {
  CUSTOM_BUTTON_SIZE,
  CUSTOM_BUTTON_VARIANT,
  CustomButton,
  // @ts-expect-error - no TypeScript definitions yet
} from 'src/components/CustomButton';
import { CustomIconButton } from 'src/components/CustomIconButton';
import { CustomTooltip } from 'src/components/CustomTooltip';
import { type SavedDocumentComment } from 'src/data/savedDocument';
import { type CallbackN, type VoidFunction } from 'src/lib/types';

import { formatCommentTime } from '../lib/utils';

import { CommentAuthor } from './CommentAuthor';
import { CommentInput } from './CommentInput';
import styles from './CommentItem.module.scss';

const MENU_PROPS = {
  anchorOrigin: { vertical: 'bottom', horizontal: 'right' },
  transformOrigin: { vertical: 'top', horizontal: 'right' },
  getContentAnchorEl: null,
  // Edit focuses its input while the menu closes. The menu must not take the focus back.
  disableEnforceFocus: true,
  disableRestoreFocus: true,
};

interface CommentItemProps {
  className?: string;
  comment: SavedDocumentComment;
  onResolve?: VoidFunction;
  onEdit: CallbackN<[string]>;
  onDelete: VoidFunction;
}

export const CommentItem = ({
  className,
  comment,
  onResolve,
  onEdit,
  onDelete,
}: CommentItemProps) => {
  const [draft, setDraft] = useState<string | null>(null);

  const saveDraft = () => {
    if (draft?.trim()) {
      onEdit(draft.trim());
    }
    setDraft(null);
  };

  const menuItems = [
    ...(comment.canEdit
      ? [
          {
            id: 'edit',
            label: 'Edit',
            icon: <IconEdit2_16 />,
            onClick: () => setDraft(comment.text),
          },
        ]
      : []),
    ...(comment.canDelete
      ? [
          {
            id: 'delete',
            label: <span className={styles.danger}>Delete</span>,
            icon: <IconTrash16 className={styles.danger} />,
            onClick: onDelete,
          },
        ]
      : []),
  ];

  return (
    <div className={cx(styles.root, className)}>
      <CommentAuthor
        user={comment.author}
        subtitle={formatCommentTime(comment.createdAt)}
        actions={
          <>
            {menuItems.length > 0 && (
              <ContextMenu
                items={menuItems}
                menuProps={MENU_PROPS}
                renderButton={(props: object) => (
                  <CustomIconButton
                    size="small"
                    {...props}
                  >
                    <IconMoreHorizontal_24 className={styles.menuIcon} />
                  </CustomIconButton>
                )}
              />
            )}
            {onResolve && (
              <CustomTooltip title="Resolve">
                <CustomIconButton
                  size="small"
                  onClick={onResolve}
                >
                  <IconCheck_24 className={styles.resolveIcon} />
                </CustomIconButton>
              </CustomTooltip>
            )}
          </>
        }
      />

      {draft === null ? (
        <p
          className={styles.text}
          data-testid="savedDocumentCommentText"
        >
          {comment.text}
        </p>
      ) : (
        <div className={styles.editor}>
          <CommentInput
            value={draft}
            placeholder="Edit comment"
            onChange={setDraft}
            onSubmit={saveDraft}
            onCancel={() => setDraft(null)}
            autoFocus
          />
          <div className={styles.editorActions}>
            <CustomButton
              size={CUSTOM_BUTTON_SIZE.small}
              variant={CUSTOM_BUTTON_VARIANT.text}
              onClick={() => setDraft(null)}
            >
              Cancel
            </CustomButton>
            <CustomButton
              size={CUSTOM_BUTTON_SIZE.small}
              disabled={!draft.trim()}
              onClick={saveDraft}
            >
              Save
            </CustomButton>
          </div>
        </div>
      )}
    </div>
  );
};
